import { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Space, Tag, Tooltip, Typography, message } from 'antd';
import styled from 'styled-components';
import { dateToString, getFullName } from '@/utils';
import { DownloadSvg, CheckSvg } from '@/assets/icons';
import Table from '@/components/table';
import Button from '@/components/button';
import { useDownloadParentsByStatusMutation, useDownloadParentsByInquiryStatusMutation } from '@/services/parent-api';
import CompleteModal from './complete-inquiry-modal';

const { Text } = Typography;

const Wrapper = styled.div`
  padding: 0 24px 24px 24px;
  background-color: var(--colorBgContainer);
  border-radius: var(--borderRadius);
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-block: 16px;
  padding-right: 8px;

  .title {
    font-size: 16px;
    font-weight: 500;
    margin-right: auto;
  }
`;

const QuickViewResults = ({ status, inquiryStatus, reportType, data, title, refetch, liaisonId, loading }) => {
  const [downloadByStatus, { isDownloadingByStatus }] = useDownloadParentsByStatusMutation();
  const [downloadByInquiryStatus, { isDownloadingByInqiryStatus }] = useDownloadParentsByInquiryStatusMutation();
  const [selectedParentInquiryId, setSelectedParentInquiryId] = useState();

  const downloadToExcel = async () => {
    try {
      let resData;
      if (status) {
        resData = await downloadByStatus({ statusKey: status, reportType, liaisonId }).unwrap();
      } else if (inquiryStatus) {
        resData = await downloadByInquiryStatus({ inquiryStatusKey: inquiryStatus, reportType, liaisonId }).unwrap();
      }
      if (resData) {
        // Convert Base64 string to a Blob
        const byteCharacters = atob(resData.fileBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const fileBlob = new Blob([byteArray], { type: resData.contentType });

        // Create a download link and click it programmatically
        const url = window.URL.createObjectURL(fileBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', resData.fileName);
        document.body.appendChild(link);
        link.click();

        // Clean up by revoking the Object URL and removing the link element
        window.URL.revokeObjectURL(url);
        link.parentNode.removeChild(link);
        message.success('Completed!');
      }
    } catch (error) {
      console.error('Download error:', error);
      // Handle error appropriately
    }
  };

  const columns1 = [
    {
      title: 'Primary Name',
      key: 'primary',
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Link to={`/parent/${record.parentId}`}>{getFullName(record.primary.name)}</Link>
          {record.inactiveDate && (
            <Text type="danger" italic>
              Inactive
            </Text>
          )}
          {record.matchedDate && <Tag color="green">Matched</Tag>}
          {record.placementCapacityDate && <Tag color="warning">{record.placementCapacityNote}</Tag>}
        </Space>
      ),
    },
    {
      title: 'Significant Other',
      dataIndex: 'significantOther',
      render: (value) => getFullName(value?.name),
    },
    {
      title: 'Adoption Liaison & County',
      dataIndex: 'county',
      render: (county, record) => (
        <>
          <div>{getFullName(record.adoptionLiaison?.name)}</div>
          <div>
            {county.name} <i>({county.region.name})</i>
          </div>
        </>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <>
          <div>{record.homeStudy.status.name}</div>
          <i>{record.adoptionRecommendation.recommended ? 'Recommended' : 'Not Recommended'}</i>
        </>
      ),
    },
    {
      title: 'Family Status',
      key: 'familyStatus',
      render: (_, record) => record.homeStudy.type.name,
    },
  ];

  const columns2 = [
    {
      title: 'Inquiry Date',
      dataIndex: 'inquiryDate',
      render: (value) => dateToString(value),
    },
    {
      title: 'Primary Name',
      key: 'primary',
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Link to={`/parent/${record.parentId}`}>{getFullName(record.primary.name)}</Link>
          {record.inactiveDate && (
            <Text type="danger" italic>
              Inactive
            </Text>
          )}
          {record.matchedDate && <Tag color="green">Matched</Tag>}
          {record.placementCapacityDate && <Tag color="warning">{record.placementCapacityNote}</Tag>}
        </Space>
      ),
    },
    {
      title: 'Significant Other',
      dataIndex: 'significantOther',
      render: (value) => getFullName(value?.name),
    },
    {
      title: 'Email',
      dataIndex: 'contact',
      render: (value) => <Link to={`mailto:${value.email.primary}`}>{value.email.primary}</Link>,
    },
    {
      title: 'Adoption Liaison & County',
      dataIndex: 'county',
      render: (county, record) => (
        <>
          <div>{getFullName(record.adoptionLiaison?.name)}</div>
          <div>
            {county.name} <i>({county.region.name})</i>
          </div>
        </>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <>
          <div>{record.homeStudy.status.name}</div>
          <i>{record.adoptionRecommendation.recommended ? 'Recommended' : 'Not Recommended'}</i>
        </>
      ),
    },
    {
      title: 'Child Number',
      dataIndex: 'children',
      render: (value) => (
        <>
          {value.map((item) => (
            <div>{item.childCode}</div>
          ))}
        </>
      ),
    },
    {
      title: 'Child',
      dataIndex: 'children',
      render: (value) => (
        <>
          {value.map((item) => (
            <div>{getFullName(item.name)}</div>
          ))}
        </>
      ),
    },
    {
      key: 'completeInquiry',
      render: (_, record) => (
        <>
          <Button
            type="primary"
            icon={<CheckSvg />}
            style={{ whiteSpace: 'normal', width: '130px', height: '50px' }}
            onClick={() => setSelectedParentInquiryId({ parentId: record.parentId, inquiryId: record.inquiryId })}
          >
            Complete this Inquiry
          </Button>
        </>
      ),
    },
  ];

  return (
    <Wrapper id="quick-view-results">
      <Toolbar>
        <div className="title">{title}</div>
        {/* {permissions.writeParent && (
          <Button type="primary" icon={<PlusSvg />} to="/parent/new">
            Add
          </Button>
        )} */}
        <Tooltip title="Download Results">
          <Button
            type="link"
            icon={<DownloadSvg fontSize="18" />}
            disabled={!data?.count}
            onClick={downloadToExcel}
            loading={isDownloadingByStatus || isDownloadingByInqiryStatus}
          />
        </Tooltip>
      </Toolbar>

      <Table
        columns={status ? columns1 : columns2}
        dataSource={data?.data}
        rowKey={status ? 'parentId' : 'inquiryId'}
        loading={loading}
        pageInfo={data}
        expandable={{ expandIcon: () => null }}
      />

      <CompleteModal
        selectedParentInquiryId={selectedParentInquiryId}
        refetch={refetch}
        onClose={() => setSelectedParentInquiryId()}
      />
    </Wrapper>
  );
};

export default memo(QuickViewResults);
